import {
  FieldTitle,
  required as requiredValidator,
  useInput,
  useResourceContext,
  ValidationError,
} from 'ra-core';
import { useWatch } from 'react-hook-form';
import { DateRangeField } from './DateRangeField';
import { FormControl, FormError, FormField, FormLabel } from '../admin/form';

export interface DateRangeInputProps {
  startSource: string;
  endSource: string;
  label?: string;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  required?: boolean;
  alwaysOn?: boolean;
}

export function DateRangeInput({
  startSource,
  endSource,
  label,
  minDate,
  maxDate,
  disabled,
  readOnly,
  error,
  required,
  alwaysOn,
}: DateRangeInputProps) {
  const resource = useResourceContext();
  const startDateInput = useInput({
    alwaysOn,
    source: startSource,
    isRequired: required,
    validate: required ? requiredValidator() : undefined,
    disabled,
    readOnly,
  });
  const endDateInput = useInput({
    alwaysOn,
    source: endSource,
    isRequired: required,
    validate: required ? requiredValidator() : undefined,
    disabled,
    readOnly,
  });
  const startDate = useWatch({ name: startSource });
  const endDate = useWatch({ name: endSource });

  const labelId =
    label === undefined ? undefined : `${startDateInput.id}-label`;
  const hasError =
    error ||
    startDateInput.fieldState.invalid ||
    endDateInput.fieldState.invalid;
  const endError = endDateInput.fieldState.error?.message;

  return (
    <FormField id={startDateInput.id} name={startDateInput.field.name}>
      {label !== undefined && (
        <FormLabel id={labelId}>
          <FieldTitle
            label={label}
            source={startSource}
            resource={resource}
            isRequired={startDateInput.isRequired}
          />
        </FormLabel>
      )}
      <FormControl>
        <DateRangeField
          startDate={toOptionalString(startDate)}
          endDate={toOptionalString(endDate)}
          onStartDateChange={(date) => {
            startDateInput.field.onChange(date);
          }}
          onEndDateChange={(date) => {
            endDateInput.field.onChange(date);
          }}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          readOnly={readOnly}
          error={hasError}
          required={required}
          inputId={startDateInput.id}
          labelId={labelId}
          inputRef={startDateInput.field.ref}
          ariaDescribedBy={`${startDateInput.id}-message`}
        />
      </FormControl>
      <FormError />
      {!startDateInput.fieldState.invalid &&
        endDateInput.fieldState.invalid && (
          <p
            id={`${startDateInput.id}-message`}
            className="text-destructive text-sm"
          >
            <ValidationError
              error={
                endError ?? {
                  message: 'moonstar.dateRange.invalid',
                  args: { _: 'Invalid date range' },
                }
              }
            />
          </p>
        )}
    </FormField>
  );
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
